import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { SUPPORTED_LOCALES, LOCALE_STORAGE_KEY } from "../i18n";

/**
 * LanguageSwitcher — segmented pill control (DE · EN · HR).
 *
 * Premium SaaS look (MediaOS-style): a rounded capsule with three
 * options and an animated highlight that slides to the active language
 * (framer-motion `layoutId`). Active = DealAlert cyan; inactive =
 * transparent with a soft hover. No native <select> / browser styles.
 *
 * Persists the choice to localStorage (read by
 * `i18next-browser-languagedetector`) and fires a custom event so
 * AuthContext can mirror the value to the backend
 * (`PATCH /api/users/me/language`). Single i18n system — no legacy
 * landing-locale sync anymore.
 */
const LABELS = { de: "DE", en: "EN", hr: "HR" };
const FULL_NAMES = { de: "Deutsch", en: "English", hr: "Hrvatski" };

export default function LanguageSwitcher({ className = "" }) {
  const { i18n } = useTranslation();
  const current = (i18n.resolvedLanguage || i18n.language || "de").split("-")[0];
  const active = SUPPORTED_LOCALES.includes(current) ? current : "de";

  const setLang = (next) => {
    if (next === active) return;
    i18n.changeLanguage(next);
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      // localStorage disabled (private mode, etc.) — ignore.
    }
    window.dispatchEvent(
      new CustomEvent("dealalert:locale-changed", { detail: { locale: next } }),
    );
  };

  return (
    <div
      role="group"
      aria-label="Language"
      data-testid="language-switcher"
      className={`inline-flex h-9 items-center gap-0.5 rounded-full border border-white/10 bg-white/[0.05] p-1 backdrop-blur ${className}`}
    >
      {SUPPORTED_LOCALES.map((code) => {
        const isActive = code === active;
        return (
          <button
            key={code}
            type="button"
            onClick={() => setLang(code)}
            data-testid={`language-option-${code}`}
            aria-pressed={isActive}
            aria-label={FULL_NAMES[code]}
            title={FULL_NAMES[code]}
            className={`relative inline-flex h-7 min-w-[2.1rem] items-center justify-center rounded-full px-2.5 text-xs font-semibold tracking-wide transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 ${
              isActive
                ? "text-slate-900"
                : "text-zinc-400 hover:text-zinc-100"
            }`}
          >
            {isActive && (
              <motion.span
                layoutId="lang-active-pill"
                className="absolute inset-0 rounded-full bg-cyan-400 shadow-[0_0_18px_-4px_rgba(34,211,238,0.75)]"
                transition={{ type: "spring", stiffness: 420, damping: 34 }}
              />
            )}
            <span className="relative z-10">{LABELS[code]}</span>
          </button>
        );
      })}
    </div>
  );
}
